// @ts-nocheck

import { FormikProvider, useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { WizardContext } from "react-albus";

import { Forms, toast } from "components";
import signup from "services/signup";

import Content from "components/Content";

import { Action, Divider } from "components";

import { partnerSchema, schemaByPartnerType } from "../../schema";
import { useSignup } from "../../store";

import { Individual } from "./Individual";
import { LegalEntity } from "./LegalEntity";
import { initialValues } from "./utils";

import { formatToCEP, formatToPhone } from "brazilian-values";
import Button from "components/Button";
import * as S from "./styles";

export type PartnerProps = {
  wizard: WizardContext;
};

const parseDate = (date: string) =>
  date.split("T")[0].split("-").reverse().join("/");

export const Partner = ({ wizard }: PartnerProps) => {
  const { setValues, getValues } = useSignup();

  const values = useMemo(() => {
    return getValues("partner");
  }, [getValues]);

  const [status, setStatus] = useState<
    "fetching-details" | "fulfilled" | "editing" | ""
  >("");

  const [partnerType, setPartnerType] = useState<
    "individual" | "legal_entity" | ""
  >("");

  const maxPartnerPercentage = (values: any[]) => {
    return (
      values.reduce((prev, current) => {
        return prev + Number(current.percentage.replace(/\D/, ""));
      }, 0) > 100
    );
  };

  const form = useFormik({
    // @ts-ignore
    initialValues: values || { document: "" },
    // @ts-ignore
    validationSchema: schemaByPartnerType[partnerType],
    validateOnBlur: true,
    onSubmit: (values) => {
      if (
        partnerType === "legal_entity" &&
        maxPartnerPercentage(values.partners)
      ) {
        toast(
          "A soma da porcentagem de todos os sócios não podem ultrapassar 100%, ajuste para continuar.",
          {
            type: "error",
          }
        );

        return;
      }

      setValues("partner", {
        ...values,
        partnerType,
      });
      wizard.next();
    },
  });

  const rules = {
    maxPartnerPercentage: () => maxPartnerPercentage(form.values.partners),
  };

  const document = useMemo(() => form.values.document, [form.values.document]);

  const getPersonDetailsByDocument = async () => {
    setPartnerType("");
    setStatus("");

    if (form.errors.document) {
      return;
    }

    try {
      setStatus("fetching-details");

      const partnerType = document.length > 14 ? "legal_entity" : "individual";

      setPartnerType(partnerType);

      const partner = await {
        individual: () => signup.findPersonByDocument(document),
        legal_entity: () => signup.findCompanyByDocument(document),
      }[partnerType]();

      form.setFieldValue("name", partner.name);

      if (partnerType === "legal_entity") {
        form.setFieldValue(
          "foundation_date",
          parseDate(partner.foundation_date)
        );

        for (const [key, value_] of Object.entries(partner.address)) {
          const value = key === "zipcode" ? formatToCEP(value_) : value_;
          form.setFieldValue(`address.${key}`, value);
        }

        form.setFieldValue("activity", partner.cnae);
      }

      if (partnerType === "individual") {
        form.setFieldValue("birthdate", parseDate(partner.birthdate));
      }

      form.setFieldValue("phone", formatToPhone(partner.phone));
    } finally {
      setStatus("fulfilled");
    }
  };

  useEffect(() => {
    if (document) {
      setStatus("");
    }
  }, [document]);

  useEffect(() => {
    if (values) {
      setStatus("editing");
      setPartnerType(values.partnerType);
    }
  }, []); // eslint-disable-line

  useEffect(() => {
    if (values?.document !== form.values.document && partnerType) {
      for (const [key, value] of Object.entries(initialValues(partnerType))) {
        form.setFieldValue(key, value);
      }
    }
  }, [values, form.values.document, partnerType]); // eslint-disable-line

  return (
    <FormikProvider value={form}>
      <form onSubmit={form.handleSubmit}>
        <S.Search.Wrap>
          <S.Search.Field>
            <Forms.InputGroup
              size="block"
              name="document"
              label="CPF/CNPJ"
              placeholder="Digite o CPF/CNPJ"
              mask="document"
            />
          </S.Search.Field>

          <S.Search.Action>
            <Button
              type="button"
              icon="Search"
              isDisabled={form.errors.document}
              onClick={getPersonDetailsByDocument}
            >
              Buscar
            </Button>
          </S.Search.Action>
        </S.Search.Wrap>

        {status === "fetching-details" && (
          <Content.Loader message="Aguarde, estamos buscando as informações do CPF/CNPJ." />
        )}

        {["fulfilled", "editing"].includes(status) && (
          <>
            <Divider title="Dados básicos" />

            <React.Suspense fallback={<p>carregando...</p>}>
              {React.createElement(
                {
                  individual: Individual,
                  legal_entity: LegalEntity,
                }[partnerType],
                { rules }
              )}
            </React.Suspense>

            <Action
              back={{
                handler: wizard.previous,
              }}
              next="Dados bancários"
            />
          </>
        )}
      </form>
    </FormikProvider>
  );
};
